import { Injectable } from '@angular/core';
import { GridInput } from '@it2go/data-grid';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import {
    SocialFinancialDepositCreateInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-financial-deposit.create-input';
import {
    SocialFinancialDepositUpdateInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-financial-deposit.update-input';
import {
    SocialFinancialDepositNextSequenceNumberInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-financial-deposit.next-sequence-number-input';
import {
    SocialFinancialDepositGridObject,
    SocialFinancialDepositObject,
} from '@libs/types/src/services/object-type/folder/social-financial-deposit.object';
import {
    SocialFinancialDepositCategoryCreateInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-financial-deposit-category.create-input';
import {
    SocialFinancialDepositCategoryUpdateInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-financial-deposit-category.update-input';
import {
    SocialFinancialDepositCategoryGridObject,
    SocialFinancialDepositCategoryObject,
} from '@libs/types/src/services/object-type/folder/social-financial-deposit-category.object';
import {
    SocialAccountCreateInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-account.create-input';
import {
    SocialAccountUpdateInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-account.update-input';
import {
    SocialAccountGridObject,
} from '@libs/types/src/services/object-type/service/settings/social-account.object';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { filterFrag } from './types/filter.types';
import {
    SocialFinancialDepositBalanceInput,
} from '@libs/types/src/services/input-type/social/social-financial-deposit/social-financial-deposit-balance.input';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';

@Injectable({ providedIn: 'root' })
export class SocialFinancialDepositGqlServiceV2 extends GqlServiceV2 {

    public getSocialFinancialDeposits(input: GridInput | null, frag: string) {
        return this.query<SocialFinancialDepositGridObject>(
            `
                query($input: GridInput!) {
                    socialFinancialDeposit {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialFinancialDeposit(input: SocialFinancialDepositCreateInput, frag: string) {
        return this.mutate<SocialFinancialDepositObject>(
            `
                mutation($input: SocialFinancialDepositCreateInput!) {
                    socialFinancialDeposit {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialFinancialDepositNextSequenceNumber(input: SocialFinancialDepositNextSequenceNumberInput): Promise<number> {
        return this.query<number>(
            `
                query($input: SocialFinancialDepositNextSequenceNumberInput!) {
                    socialFinancialDeposit {
                        nextSequenceNumber(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public getSocialFinancialDepositBalance(input: SocialFinancialDepositBalanceInput): Promise<number> {
        return this.query<number>(
            `
                query($input: SocialFinancialDepositBalanceInput!) {
                    socialFinancialDeposit {
                        financialDepositBalance(input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public updateSocialFinancialDeposit(input: SocialFinancialDepositUpdateInput, frag: string) {
        return this.mutate<SocialFinancialDepositObject>(
            `
                mutation($input: SocialFinancialDepositUpdateInput!) {
                    socialFinancialDeposit {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteSocialFinancialDeposit(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    socialFinancialDeposit {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportSocialFinancialDeposit(input: IdInput): Promise<FolderPrintObject> {
        return this.query<FolderPrintObject>(
            `
                query($input: IdInput!) {
                    socialFinancialDeposit {
                        export(input: $input) {
                            fileName
                            pdfBase64
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialFinancialDepositCategories(input: GridInput | null, frag: string) {
        return this.query<SocialFinancialDepositCategoryGridObject>(
            `
                query($input: GridInput!) {
                    socialFinancialDepositCategory {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialFinancialDepositCategory(input: SocialFinancialDepositCategoryCreateInput, frag: string) {
        return this.mutate<SocialFinancialDepositCategoryObject>(
            `
                mutation($input: SocialFinancialDepositCategoryCreateInput!) {
                    socialFinancialDepositCategory {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateSocialFinancialDepositCategory(input: SocialFinancialDepositCategoryUpdateInput, frag: string) {
        return this.mutate<SocialFinancialDepositCategoryObject>(
            `
                mutation($input: SocialFinancialDepositCategoryUpdateInput!) {
                    socialFinancialDepositCategory {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteSocialFinancialDepositCategory(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    socialFinancialDepositCategory {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSocialAccounts(input: GridInput | null, frag: string) {
        return this.query<SocialAccountGridObject>(
            `
                query($input: GridInput!) {
                    socialAccount {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createSocialAccount(input: SocialAccountCreateInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialAccountCreateInput!) {
                    socialAccount {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateSocialAccount(input: SocialAccountUpdateInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: SocialAccountUpdateInput!) {
                    socialAccount {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteSocialAccount(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    socialAccount {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

}
